import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { focusValidationError } from '../../../../lib/validationFocus';
import { budgetReviewApi } from '../budgetReviewApi';
import type { BudgetReviewDepartmentDetail } from '../budgetReviewTypes';

/**
 * The key a fund source's field is stored and addressed under — its expense class, since the
 * fund source IS the class (06 Oct 2026).
 */
const bucket = (expenseClass: number) => String(expenseClass);

/**
 * Sets ONE department's approved ceilings — every fund it proposed against, in one form.
 *
 * **The whole department at once, because the approval guard reads the whole department at
 * once.** A per-fund form would let a reviewer price one of three funds, leave the modal
 * believing they had finished, and meet a refusal on Approve naming a department they thought
 * was done. One request, one decision, one place the refusal can point at.
 *
 * **A BLANK field leaves that fund exactly where it is**, and the form says so rather than
 * leaving it to be discovered. It is the same rule the government-ID number follows one suite
 * along: a form that cannot see a value must not be able to destroy it, and blank here means
 * *not supplied* rather than *cleared*. Clearing a ceiling back to unpriced has no affordance,
 * which is a real gap rather than a decision.
 *
 * **The PROPOSED figures are stated, never collected.** They belong to the department and this
 * form does not write them — the Table Display View, which is what read-only facts take
 * wherever they are shown.
 *
 * **NO Autogenerate, and the exemption is `<ResetUserPasswordModal>`'s**: nothing on this form
 * is required, so the primary action enables on the first try and the button would have
 * nothing to do. It is also the one form where a generator would be actively wrong — the
 * honest value to fill is the proposed figure, and that is a REVIEWER'S VERDICT rather than
 * sample data. `Match proposed` is that affordance, drawn as the ordinary control it is.
 */
export function BudgetReviewCeilingModal({
  open,
  budgetPlanId,
  orgUnitId,
  orgUnitName,
  onClose,
  onSaved,
}: {
  open: boolean;
  budgetPlanId: string;
  orgUnitId: string;
  /** Stated while the detail loads, so the title is never a placeholder. */
  orgUnitName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [detail, setDetail] = useState<BudgetReviewDepartmentDetail | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    The department's fund sources are FETCHED rather than taken from the roster row: the row
    carries one summed ceiling, which is what a column can hold. Pricing needs one recorded
    ceiling per class, and that is a different question from the one the list answers.
  */
  useEffect(() => {
    const controller = new AbortController();

    setDetail(null);
    setError(null);

    budgetReviewApi
      .department(budgetPlanId, orgUnitId, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;

        setDetail(loaded);

        // Seeded from what is RECORDED, so the form states the ceilings already set rather
        // than opening blank over them — which would read as nothing having been priced.
        setAmounts(
          Object.fromEntries(
            loaded.lines.map((line) => [
              bucket(line.expenseClass),
              line.approvedCeiling === null ? '' : String(line.approvedCeiling),
            ]),
          ),
        );
      })
      .catch((caught) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('Could not load this department.', 0),
        );
      });

    return () => controller.abort();
  }, [budgetPlanId, orgUnitId]);

  /** A field that is blank is NOT SENT; anything else has to be a number of zero or more. */
  const parsed = Object.entries(amounts).map(([key, raw]) => {
    const trimmed = raw.trim();

    if (trimmed === '') return { key, supplied: false, value: 0, valid: true };

    const value = Number.parseFloat(trimmed.replace(/[^0-9.-]/g, ''));

    return { key, supplied: true, value, valid: Number.isFinite(value) && value >= 0 };
  });

  const allValid = parsed.every((entry) => entry.valid);
  const canSave = detail !== null && detail.isReviewable && allValid && confirmed && !saving;

  function matchProposed() {
    if (detail === null) return;

    setAmounts(
      Object.fromEntries(
        detail.lines.map((line) => [bucket(line.expenseClass), String(line.proposed)]),
      ),
    );
  }

  async function save() {
    if (!canSave || detail === null) return;

    setSaving(true);
    setError(null);

    try {
      await budgetReviewApi.setCeiling(budgetPlanId, orgUnitId, {
        funds: detail.lines
          .map((line) => ({
            line,
            entry: parsed.find((p) => p.key === bucket(line.expenseClass)),
          }))
          .filter(({ entry }) => entry?.supplied === true)
          .map(({ line, entry }) => ({
            expenseClass: line.expenseClass,
            amount: entry!.value,
          })),
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the ceilings.', 0);

      setError(failure);

      /*
        Every rejection here names `funds`, because the request carries several of them and
        the modal is ONE control over the set — pointing at a per-fund field would name an id
        the server has never seen. `ceiling-funds` is the heading of that block.
      */
      focusValidationError(failure, {
        idPrefix: 'ceiling',
        fieldIds: { funds: 'ceiling-funds' },
      });
    } finally {
      setSaving(false);
    }
  }

  const lockedReason = detail !== null && !detail.isReviewable;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Approved ceiling — ${orgUnitName}`}
      size="2xl"
      id="ceiling-modal"
      confirm={{
        id: 'ceiling-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving || detail === null,
      }}
      footer={
        <>
          <Button id="ceiling-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ceiling-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" id="ceiling-funds">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {detail === null && error === null ? (
          // The skeleton is the SHAPE of what it replaces — a facts table over a run of
          // fields — so the modal does not change size as the detail lands.
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </>
        ) : null}

        {detail !== null ? (
          <>
            {/*
              WHAT THIS FORM DOES NOT COLLECT, stated rather than offered as locked controls.
              The plan and the department were chosen by choosing whose row to open.
            */}
            <PreviewTable
              variant="table-display"
              rows={[
                { label: 'Budget plan', value: detail.planNumber, mono: true },
                { label: 'Department', value: detail.orgUnitName },
                { label: 'PPAs filed', value: String(detail.ppaCount), mono: true },
              ]}
            />

            {lockedReason ? (
              <p
                role="alert"
                className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
              >
                This plan is no longer under review, so its ceilings can no longer be set.
                Return it to the departments for update to reopen the review.
              </p>
            ) : null}

            {detail.lines.length === 0 ? (
              <p className="rounded-lg border border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
                This department has proposed nothing under this plan, so there is nothing to
                price.
              </p>
            ) : null}

            {detail.lines.map((line) => {
              const key = bucket(line.expenseClass);
              const entry = parsed.find((p) => p.key === key);

              return (
                <div key={key} className="flex flex-col gap-2">
                  {/*
                    The fund source's own PROPOSAL — what the department asked for under this
                    expense class, beside the one figure a ceiling has. The fund source IS the
                    class, so there is no per-class split beneath it.
                  */}
                  <PreviewTable
                    variant="table-display"
                    labelWidth="narrow"
                    rows={[{ label: 'Proposed', value: formatMoney(line.proposed), mono: true }]}
                  />

                  <TextField
                    id={`ceiling-amount-${key}`}
                    label={line.expenseClassLabel}
                    description="Blank leaves this fund source's ceiling exactly as it is. Zero is a real answer — it approves nothing against this fund source."
                    value={amounts[key] ?? ''}
                    onChange={(e) => setAmounts((prev) => ({ ...prev, [key]: e.target.value }))}
                    error={
                      entry?.valid === false ? 'Enter an amount of zero or more.' : undefined
                    }
                    disabled={saving || lockedReason}
                  />
                </div>
              );
            })}

            {detail.lines.length > 0 ? (
              /*
                MATCH PROPOSED is an ordinary control, not Autogenerate. Approval is an
                EQUALITY check — a plan cannot be approved until every department's ceiling
                equals its proposal, fund for fund — so filling the asks is the commonest
                verdict a reviewer gives, and it is a decision they are making rather than
                sample data somebody generated.
              */
              <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-600">
                  A plan can only be approved once every department's ceiling equals its
                  proposal, fund for fund.
                </p>

                <Button
                  id="ceiling-match"
                  variant="secondary"
                  onClick={matchProposed}
                  disabled={saving || lockedReason}
                >
                  Match proposed
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
